import { MonitoringPromptSummaryDto } from '@edgebox/sync-core-rest-client';

/**
 * Scoping the prompt filter to the purpose tab the reader is on.
 *
 * The AI Visibility screen is organised by prompt purpose — every figure on it is already filtered
 * to one purpose. The prompt dropdown was not, so it offered every prompt in the project including
 * ones belonging to other purposes. Choosing one of those produced an empty screen: the purpose
 * filter and the prompt filter contradicted each other, and nothing said so.
 */

/** A prompt as this screen needs it: identity plus the purposes it belongs to. */
export type ScopablePrompt = Pick<MonitoringPromptSummaryDto, 'key' | 'name'> & {
  /** Purpose term keys assigned to this prompt. Empty when it carries none. */
  purposeTermKeys: string[];
};

/**
 * The prompts of the lean prompt list as the Prompt filter offers them: by name, each with its
 * purpose keys, each key once, and none without a key. A prompt without a name is offered under its
 * key. A key read twice — a prompt added while the pages were being read shifts the next page — is
 * offered once.
 */
export function scopablePromptsOf(summaries: readonly MonitoringPromptSummaryDto[]): ScopablePrompt[] {
  return summaries
    .map((summary) => ({
      key: String(summary.key || '').trim(),
      name: String(summary.name || summary.key || '').trim(),
      purposeTermKeys: Array.from(
        new Set((summary.purposes || []).map((purpose) => String(purpose.key || '').trim()).filter((key) => !!key))
      ),
    }))
    .filter((prompt, index, prompts) => !!prompt.key && prompts.findIndex((other) => other.key === prompt.key) === index)
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * Whether a prompt belongs on the given purpose tab.
 *
 * A prompt with NO purpose assignment belongs to every tab. Purpose is assigned by the extraction
 * pipeline and is not guaranteed to be present on older prompts; scoping one of those out of every
 * tab would make it unselectable anywhere, which is a worse failure than showing it under a purpose
 * it may not strictly belong to.
 */
export function promptBelongsToPurpose(prompt: ScopablePrompt, purposeTermKey: string): boolean {
  if (!purposeTermKey) {
    return true;
  }
  return !prompt.purposeTermKeys.length || prompt.purposeTermKeys.includes(purposeTermKey);
}

/** The prompts offered on a purpose tab, in the order they should be listed. */
export function promptsForPurpose(prompts: readonly ScopablePrompt[], purposeTermKey: string): ScopablePrompt[] {
  return prompts.filter((prompt) => promptBelongsToPurpose(prompt, purposeTermKey));
}

/**
 * The prompt selection that survives a change of purpose.
 *
 * Cascades: a prompt that does not belong to the new purpose is cleared rather than left selected,
 * because leaving it would filter every figure to the intersection of two contradictory conditions
 * and report the empty result as though it were a finding. Clearing shows the whole purpose, which
 * is what the reader asked for by switching tabs.
 */
export function resolveSelectedPromptKey(prompts: readonly ScopablePrompt[], purposeTermKey: string, currentPromptKey: string): string {
  if (!currentPromptKey) {
    return '';
  }

  const selected = prompts.find((prompt) => prompt.key === currentPromptKey);
  // A prompt that is no longer in the list at all (deleted, or archived out of the fetch) is also
  // cleared — keeping it would filter by something the reader can no longer see or undo.
  if (!selected || !promptBelongsToPurpose(selected, purposeTermKey)) {
    return '';
  }

  return currentPromptKey;
}
